import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Plus, Trash2, LogOut, Users, Award, ChevronDown, Shield, UserPlus, Eye, EyeOff
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a49fb855-3c1f-4bf2-8664-337bfce9e50d/artifacts/sokgx5c0_Gold.png";

// Sidebar Logo component
const SidebarLogo = () => (
  <img 
    src={LOGO_URL} 
    alt="TC PPF" 
    className="h-9 w-auto object-contain"
    style={{ mixBlendMode: 'lighten' }}
  />
);

const AdminManagement = () => {
  const navigate = useNavigate();
  const { admin, logout, getAuthHeaders } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    is_super_admin: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admins`, {
        headers: getAuthHeaders()
      });
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      is_super_admin: false
    });
    setShowPassword(false);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    
    if (!formData.username.trim() || !formData.password.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setFormLoading(true);
    
    try {
      await axios.post(`${API}/admins`, formData, {
        headers: getAuthHeaders()
      });
      
      toast.success('Admin created successfully');
      setShowAddModal(false);
      resetForm();
      fetchAdmins();
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    setFormLoading(true);
    
    try {
      await axios.delete(`${API}/admins/${selectedAdmin.id}`, {
        headers: getAuthHeaders()
      });
      
      toast.success('Admin deleted successfully');
      setShowDeleteModal(false);
      setSelectedAdmin(null);
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
    } finally {
      setFormLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-72 bg-[#050505] border-r border-white/10 flex flex-col py-6 px-4 z-50">
        <div className="flex items-center px-2 mb-8">
          <img 
            src={LOGO_URL} 
            alt="TC PPF" 
            className="h-12 w-auto object-contain"
            style={{ mixBlendMode: 'lighten' }}
          />
        </div>

        <nav className="flex-1 space-y-2">
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-all"
            data-testid="nav-certificates"
          >
            <Award className="w-5 h-5" />
            Certificates
          </Link>
          <Link
            to="/admin/admins"
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20"
            data-testid="nav-admins"
          >
            <Users className="w-5 h-5" />
            Admin Management
          </Link>
        </nav>

        <div className="border-t border-white/10 pt-4 mt-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-all">
                <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <span className="text-[#D4AF37] text-sm font-bold">{admin?.username?.[0]?.toUpperCase()}</span>
                </div>
                <span className="flex-1 text-left text-sm">{admin?.username}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-700">
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-72 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl text-white mb-2">Admin Management</h1>
            <p className="text-zinc-400">Manage system administrators</p>
          </div>
          <Button 
            onClick={() => { resetForm(); setShowAddModal(true); }}
            className="btn-gold"
            data-testid="add-admin-button"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Admin
          </Button>
        </div>

        {/* Admins Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full"></div>
            </div>
          ) : admins.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Users className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-500">No admins found</p>
            </div>
          ) : (
            admins.map((adminUser) => (
              <Card 
                key={adminUser.id} 
                className="glass-card group hover:border-[#D4AF37]/30 transition-all"
                data-testid={`admin-card-${adminUser.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <span className="text-[#D4AF37] text-lg font-bold">
                        {adminUser.username[0].toUpperCase()}
                      </span>
                    </div>
                    {adminUser.id !== admin?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedAdmin(adminUser);
                          setShowDeleteModal(true);
                        }}
                        className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`delete-admin-${adminUser.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  <h3 className="text-white font-semibold text-lg mb-1">{adminUser.username}</h3>
                  
                  <div className="flex items-center gap-2 mb-3">
                    {adminUser.is_super_admin ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30">
                        <Shield className="w-3 h-3" />
                        Super Admin
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-zinc-800 text-zinc-400">
                        Admin
                      </span>
                    )}
                    {adminUser.id === admin?.id && (
                      <span className="text-xs text-zinc-500">(You)</span>
                    )}
                  </div>
                  
                  <p className="text-zinc-500 text-sm">
                    Created: {formatDate(adminUser.created_at)}
                  </p>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>

      {/* Add Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add New Admin</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Username</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                placeholder="Enter username"
                className="bg-zinc-900 border-zinc-700 text-white"
                data-testid="admin-username-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  placeholder="Enter password (min 6 characters)"
                  className="bg-zinc-900 border-zinc-700 text-white pr-10"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="text-zinc-300">Super Admin</Label>
                <p className="text-xs text-zinc-500">Can manage other admins</p>
              </div>
              <Switch
                checked={formData.is_super_admin}
                onCheckedChange={(checked) => setFormData({ ...formData, is_super_admin: checked })}
                data-testid="admin-super-switch"
              />
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="text-zinc-400 hover:text-white"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="btn-gold"
                disabled={formLoading}
                data-testid="create-admin-button"
              >
                {formLoading ? (
                  <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full"></div>
                ) : 'Create Admin'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-center">Delete Admin</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-zinc-300 mb-2">
              Are you sure you want to delete the admin
            </p>
            <p className="text-white font-semibold">
              {selectedAdmin?.username}?
            </p>
            <p className="text-zinc-500 text-sm mt-2">This action cannot be undone.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowDeleteModal(false)}
              className="text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              disabled={formLoading}
              data-testid="confirm-delete-admin"
            >
              {formLoading ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
              ) : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminManagement;
